/* eslint-disable no-console */
import type { TLogEntry, TLogLevel, TLogTransport } from './logger.types';

import { redact } from './redact';

const CONSOLE_METHOD: Record<TLogLevel, (...args: unknown[]) => void> = {
  debug: console.log,
  info: console.log,
  warn: console.warn,
  error: console.error,
};

const LEVEL_RANK: Record<TLogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const isDevelopment = import.meta.env.DEV;

const MIN_LEVEL: TLogLevel = 'debug';

const consoleTransport: TLogTransport = ({ level, message, payload }) => {
  if (payload === undefined) {
    CONSOLE_METHOD[level](`[${level}]`, message);

    return;
  }

  CONSOLE_METHOD[level](`[${level}]`, message, payload);
};

// TODO(the500admin): add a remote transport when the Audit log screen lands — production stays silent until then.
const transports: TLogTransport[] = isDevelopment ? [consoleTransport] : [];

const toPayload = (payload: unknown): unknown => {
  if (payload === undefined) {
    return undefined;
  }

  if (payload instanceof Error) {
    return redact({ name: payload.name, message: payload.message, stack: payload.stack });
  }

  return redact(payload);
};

const write = (level: TLogLevel, message: string, payload?: unknown): void => {
  if (transports.length === 0 || LEVEL_RANK[level] < LEVEL_RANK[MIN_LEVEL]) {
    return;
  }

  const entry: TLogEntry = {
    level,
    message,
    payload: toPayload(payload),
  };

  transports.forEach((transport) => {
    transport(entry);
  });
};

const logger = {
  debug: (message: string, payload?: unknown): void => write('debug', message, payload),
  info: (message: string, payload?: unknown): void => write('info', message, payload),
  warn: (message: string, payload?: unknown): void => write('warn', message, payload),
  error: (message: string, payload?: unknown): void => write('error', message, payload),
};

export {
  isDevelopment,
  logger,
};
